import { useState, useEffect, useCallback, useRef } from 'react';
import { createApiInstance, handleApiError } from '../utils/api';

const decodeGoogleJwt = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const isGoogleTokenExpired = (token) => {
  if (!token || token.startsWith('local_')) return false;
  const decoded = decodeGoogleJwt(token);
  if (!decoded?.exp) return false;
  return Date.now() >= decoded.exp * 1000;
};

const googleTokenExpiresInMs = (token) => {
  if (!token || token.startsWith('local_')) return Infinity;
  const decoded = decodeGoogleJwt(token);
  if (!decoded?.exp) return 0;
  return decoded.exp * 1000 - Date.now();
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const expiryTimerRef  = useRef(null);
  const logoutTimerRef  = useRef(null);

  const createApi = useCallback(() => {
    const token = localStorage.getItem('googleToken') || localStorage.getItem('localToken');
    const instance = createApiInstance(token);

    instance.interceptors.response.use(
      (response) => response,
      (error) => handleApiError(error, setError, setUser)
    );

    return instance;
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('googleToken');
    localStorage.removeItem('localToken');
    localStorage.removeItem('googlePicture');
    setUser(null);
  }, []);

  const scheduleExpiryCheck = useCallback((token) => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (logoutTimerRef.current)  clearTimeout(logoutTimerRef.current);

    const msLeft = googleTokenExpiresInMs(token);
    if (msLeft <= 0 || msLeft === Infinity) return;

    // Avisa 10 min antes de expirar
    const warnIn = msLeft - 10 * 60 * 1000;
    if (warnIn > 0) {
      expiryTimerRef.current = setTimeout(() => {
        setError('Sua sessão expira em 10 minutos. Salve seu trabalho e faça login novamente em breve.');
      }, warnIn);
    }

    // Desloga automaticamente quando expirar — guarda ref para poder cancelar no logout
    logoutTimerRef.current = setTimeout(() => {
      clearSession();
      setError('Sua sessão expirou. Por favor, faça login novamente.');
    }, msLeft);
  }, [clearSession]);

  const loadUserData = useCallback(async (credential, isLocal = false) => {
    setLoading(true);
    setError('');
    try {
      if (isLocal) {
        localStorage.setItem('localToken', credential);
      } else {
        localStorage.setItem('googleToken', credential);
        const decoded = decodeGoogleJwt(credential);
        if (decoded?.picture) {
          localStorage.setItem('googlePicture', decoded.picture);
        }
        scheduleExpiryCheck(credential);
      }

      const api = createApi();

      let userRes;
      if (isLocal) {
        userRes = await api.get('/auth/me');
      } else {
        userRes = await api.post('/auth/google');
      }

      const userData = userRes.data.user;
      if (!userData.picture && !isLocal) {
        const storedPicture = localStorage.getItem('googlePicture');
        if (storedPicture) userData.picture = storedPicture;
      }
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Erro completo no login:', err);
      const message = err.response?.data?.error || err.message || 'Falha na autenticação';
      setError(message);
      clearSession();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [createApi, scheduleExpiryCheck, clearSession]);

  const logout = useCallback(() => {
    if (expiryTimerRef.current)  clearTimeout(expiryTimerRef.current);
    if (logoutTimerRef.current)  clearTimeout(logoutTimerRef.current);
    clearSession();
    setError('');
    window.location.reload();
  }, [clearSession]);

  useEffect(() => {
    const googleToken = localStorage.getItem('googleToken');
    const localToken = localStorage.getItem('localToken');
    const token = googleToken || localToken;

    if (token && !user) {
      if (googleToken && isGoogleTokenExpired(googleToken)) {
        clearSession();
        setError('Sua sessão expirou. Por favor, faça login novamente.');
        return;
      }
      if (googleToken) scheduleExpiryCheck(googleToken);
      loadUserData(token, !!localToken).catch(() => {
        clearSession();
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { user, loading, error, loadUserData, logout, createApi };
};
